import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { useAiTags } from '@solinkcorp/ai-tags';
import { type AiTagId, type CallhomeAiTag, type SiteId } from '@solinkcorp/api-types';
import { useSite } from '@solinkcorp/callhome-data';
import { cn } from '@solinkcorp/solink-ui/lib/utils';
import { useQueryParams } from '@solinkcorp/utils/hooks';
import { type Immutable } from 'immer';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import useDimensions from 'react-cool-dimensions';
import { useLocation, useNavigate } from 'react-router-dom';
import { useControllerState } from 'solox';
import { LiveFeedTelemetry } from '../../analytics/liveFeedTelemetry';
import { SDM_CONFIG_ROUTE } from '../../constants';
import { useServiceDisplayInjection } from '../../context/ServiceDisplayInjectionContext';
import { useAuthDevice } from '../../contexts/DeviceContext';
import { LiveFeedTelemetryProvider } from '../../contexts/LiveFeedTelemetryContext';
import { DEFAULT_LAYOUT, type Controller } from '../../Controller';
import useEventsControllerTagNamesSetter from '../../hooks/useEventsControllerTagNamesSetter';
import { useServiceDisplayDayParts } from '../../hooks/useServiceDisplayDayParts';
import useServiceDisplayEvents from '../../hooks/useServiceDisplayEvents';
import useServiceDisplayHeartbeat from '../../hooks/useServiceDisplayHeartbeat';
import useSiteSpecificVisionAnalyticsCamera, {
  type VisionAnalyticsConfig,
} from '../../hooks/useSiteSpecificVisionAnalyticsCamera';
import {
  applyInsertionPoint,
  insertionPointsEqual,
  pointerToInsertionPoint,
  pointerToWidget,
  swapWidgets,
  type InsertionPoint,
} from '../../layout/dragRows';
import { getWidgetLookbackMs } from '../../layout/getHorizonWindow';
import { groupWidgetsIntoRows, SDM_GRID_ROWS } from '../../layout/gridPositions';
import { type ServiceDisplayWidget } from '../../layout/types';
import { type ServiceDisplayEventsController } from '../../ServiceDisplayEventsController';
import Widget from '../widgets/Widget';
import EmptyLayout from './EmptyLayout';

/**
 * How close (px) the cursor must be to a slot boundary or a row edge for an
 * insertion to activate during a drag. Beyond it, nothing highlights and a drop
 * rearranges nothing.
 */
const INSERT_BUFFER_PX = 60;
/** Gap on each side of the insertion bar (Figma `alias/space/2xs`). */
const INSERT_GAP_PX = 4;
const INSERT_BAR_THICKNESS_PX = 4;
const INSERT_SLOT_PX = INSERT_GAP_PX + INSERT_BAR_THICKNESS_PX + INSERT_GAP_PX;
const LAYOUT_TRANSITION_MS = 300;

function rowTransitionClass(animateLayout: boolean) {
  if (animateLayout) return '[transition:height_300ms_ease-out,transform_300ms_ease-out]';
  return 'transition-transform duration-300 ease-out';
}

function widgetTransitionClass(animateLayout: boolean, isInvertingFlip: boolean) {
  if (isInvertingFlip) return 'transition-none';
  return 'transition-[transform,width] duration-300 ease-out';
}

interface GridProps {
  editing: boolean;
  controller: Controller;
  eventsController: ServiceDisplayEventsController;
  editMode?: boolean;
  isStandalone: boolean;
}

type Row = Immutable<ServiceDisplayWidget>[];

function WidgetRow({
  heightPx,
  translateYPx,
  animateLayout,
  children,
}: {
  heightPx: number;
  translateYPx: number;
  animateLayout: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn('flex w-full', rowTransitionClass(animateLayout))}
      style={{
        height: `${heightPx}px`,
        transform: translateYPx !== 0 ? `translateY(${translateYPx}px)` : undefined,
      }}
    >
      {children}
    </div>
  );
}

function getWidgetInsertTranslateX(
  widgetIndex: number,
  insertIndex: number,
  rowLength: number,
): number {
  if (insertIndex <= 0) return INSERT_SLOT_PX;
  if (insertIndex >= rowLength) return -INSERT_SLOT_PX;
  return widgetIndex < insertIndex ? -INSERT_SLOT_PX / 2 : INSERT_SLOT_PX / 2;
}

function getRowInsertTranslateY(rowIndex: number, insertSeam: number, rowCount: number): number {
  if (insertSeam <= 0) return INSERT_SLOT_PX;
  if (insertSeam >= rowCount) return -INSERT_SLOT_PX;
  return rowIndex < insertSeam ? -INSERT_SLOT_PX / 2 : INSERT_SLOT_PX / 2;
}

function getVerticalBarLeft(row: Row, insertIndex: number, widthPx: number): number {
  if (insertIndex <= 0) return INSERT_SLOT_PX / 2 - INSERT_BAR_THICKNESS_PX / 2;
  if (insertIndex >= row.length) {
    return widthPx - INSERT_SLOT_PX / 2 - INSERT_BAR_THICKNESS_PX / 2;
  }
  const beforeFraction = row
    .slice(0, insertIndex)
    .reduce((sum, widget) => sum + widget.position.w / 100, 0);
  return beforeFraction * widthPx - INSERT_BAR_THICKNESS_PX / 2;
}

function getHorizontalBarTop(
  insertSeam: number,
  rowCount: number,
  rowHeightPx: number,
  heightPx: number,
): number {
  if (insertSeam <= 0) return INSERT_SLOT_PX / 2 - INSERT_BAR_THICKNESS_PX / 2;
  if (insertSeam >= rowCount) return heightPx - INSERT_SLOT_PX / 2 - INSERT_BAR_THICKNESS_PX / 2;
  return insertSeam * rowHeightPx - INSERT_BAR_THICKNESS_PX / 2;
}

/**
 * Absolutely-positioned insertion bar. Widgets spread via transform (not reflow)
 * so the indicator can slide between targets without layout jank.
 */
function InsertionBar({
  point,
  rows,
  rowHeightPx,
  widthPx,
  heightPx,
}: {
  point: InsertionPoint;
  rows: readonly Row[];
  rowHeightPx: number;
  widthPx: number;
  heightPx: number;
}) {
  if (point.orientation === 'vertical') {
    const row = rows[point.rowIndex] ?? [];
    const barHeightPx = rowHeightPx * 0.36;
    return (
      <div
        aria-hidden
        className="pointer-events-none absolute z-10 w-1 rounded-full bg-primary transition-[left,top] duration-300 ease-out"
        style={{
          left: getVerticalBarLeft(row, point.insertIndex, widthPx),
          top: point.rowIndex * rowHeightPx + (rowHeightPx - barHeightPx) / 2,
          height: barHeightPx,
        }}
      />
    );
  }

  const barWidthPx = widthPx * 0.36;
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute z-10 h-1 rounded-full bg-primary transition-[left,top] duration-300 ease-out"
      style={{
        left: (widthPx - barWidthPx) / 2,
        top: getHorizontalBarTop(point.rowIndex, rows.length, rowHeightPx, heightPx),
        width: barWidthPx,
      }}
    />
  );
}

function DraggableWidgetCard({
  widget,
  disabled,
  isActive,
  isSwapTarget,
  translateXPx,
  layoutFlip,
  animateLayout,
  onRegisterRef,
  children,
}: {
  widget: Row[number];
  /** True for the widget currently being dragged: its slot stays sized but empty. */
  isActive: boolean;
  /** True for the widget the drag would swap with on drop. */
  isSwapTarget: boolean;
  disabled: boolean;
  translateXPx: number;
  layoutFlip: { x: number; y: number };
  animateLayout: boolean;
  onRegisterRef: (id: string, el: HTMLDivElement | null) => void;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: widget.id ?? '',
    disabled,
  });

  const widgetId = widget.id ?? '';
  const transforms: string[] = [];
  if (layoutFlip.x !== 0 || layoutFlip.y !== 0) {
    transforms.push(`translate(${layoutFlip.x}px, ${layoutFlip.y}px)`);
  }
  if (translateXPx !== 0) {
    transforms.push(`translateX(${translateXPx}px)`);
  }

  const isInvertingFlip = layoutFlip.x !== 0 || layoutFlip.y !== 0;

  return (
    <div
      ref={(el) => {
        setNodeRef(el);
        onRegisterRef(widgetId, el);
      }}
      data-testid="sdm-widget-card"
      className={cn('relative h-full', widgetTransitionClass(animateLayout, isInvertingFlip))}
      {...attributes}
      {...listeners}
      style={{
        width: `${widget.position.w}%`,
        transform: transforms.length > 0 ? transforms.join(' ') : undefined,
      }}
    >
      {/* The dragged widget follows the cursor in the DragOverlay, so its slot
          here renders empty — the layout keeps its shape while the origin reads
          as vacated space. */}
      {!isActive && children}
      {/* Swap-target highlight sits above the widget content (a tint on the card
          itself would be painted over by the widget). Always rendered so the
          teal fades in/out via transition-colors instead of popping. */}
      <div
        data-testid="sdm-widget-swap-target-highlight"
        data-swap-target={isSwapTarget}
        className={cn(
          'pointer-events-none absolute inset-0 z-10 transition-colors duration-300 ease-out',
          isSwapTarget ? 'bg-background-accent/50' : 'bg-transparent',
        )}
      />
    </div>
  );
}

interface GridWidgetRowProps {
  row: Row;
  rowIndex: number;
  rowHeightPx: number;
  rowCount: number;
  activeInsertionPoint: InsertionPoint | null;
  activeId: string | null;
  swapTargetId: string | null;
  layoutFlip: Map<string, { x: number; y: number }>;
  animateLayout: boolean;
  dragDisabled: boolean;
  registerWidgetRef: (id: string, el: HTMLDivElement | null) => void;
  controller: Controller;
  eventsController: ServiceDisplayEventsController;
  allTags: readonly CallhomeAiTag[];
  aiTagConfigurationMap: Record<AiTagId, VisionAnalyticsConfig>;
  isDataLoading: boolean;
}

function GridWidgetRow({
  row,
  rowIndex,
  rowHeightPx,
  rowCount,
  activeInsertionPoint,
  activeId,
  swapTargetId,
  layoutFlip,
  animateLayout,
  dragDisabled,
  registerWidgetRef,
  controller,
  eventsController,
  allTags,
  aiTagConfigurationMap,
  isDataLoading,
}: GridWidgetRowProps) {
  const verticalInsert =
    activeInsertionPoint?.orientation === 'vertical' && activeInsertionPoint.rowIndex === rowIndex
      ? activeInsertionPoint
      : null;
  const horizontalInsertSeam =
    activeInsertionPoint?.orientation === 'horizontal' ? activeInsertionPoint.rowIndex : null;
  const rowTranslateYPx =
    horizontalInsertSeam !== null
      ? getRowInsertTranslateY(rowIndex, horizontalInsertSeam, rowCount)
      : 0;

  return (
    <WidgetRow heightPx={rowHeightPx} translateYPx={rowTranslateYPx} animateLayout={animateLayout}>
      {row.map((widget, widgetIndex) => {
        const isActive = activeId === widget.id;
        const translateXPx = verticalInsert
          ? getWidgetInsertTranslateX(widgetIndex, verticalInsert.insertIndex, row.length)
          : 0;
        const widgetFlip = layoutFlip.get(widget.id ?? '') ?? { x: 0, y: 0 };

        return (
          <DraggableWidgetCard
            key={widget.id}
            widget={widget}
            disabled={dragDisabled}
            isActive={isActive}
            isSwapTarget={swapTargetId === widget.id}
            translateXPx={translateXPx}
            layoutFlip={widgetFlip}
            animateLayout={animateLayout}
            onRegisterRef={registerWidgetRef}
          >
            <Widget
              controller={controller}
              eventsController={eventsController}
              allTags={allTags}
              aiTagConfigurationMap={aiTagConfigurationMap}
              widget={widget}
              isDataLoading={isDataLoading}
            />
          </DraggableWidgetCard>
        );
      })}
    </WidgetRow>
  );
}

export default function Grid({
  editing,
  controller,
  eventsController,
  editMode,
  isStandalone,
}: GridProps) {
  const { height, width, observe: gridRef } = useDimensions();
  const navigate = useNavigate();
  const [query] = useQueryParams();
  const location = useLocation();
  const siteId = (query.get('siteId') ?? undefined) as SiteId | undefined;
  const { site } = useSite({ siteId });
  const device = site?.devices?.[0];
  const { data: dayParts = [] } = useServiceDisplayDayParts();

  const editingLayout = useControllerState(controller.state, (state) => state.editingLayout);
  const stateSelectedLayout = useControllerState(controller.state, (state) => state.selectedLayout);

  const currentLayout = useMemo(() => stateSelectedLayout || DEFAULT_LAYOUT, [stateSelectedLayout]);
  const selectedLayout = editingLayout ?? currentLayout;

  const lookbackMs = useMemo(() => {
    let max = 0;
    for (const w of selectedLayout.widgets) {
      // Live feed widgets draw no events, so they must not widen the event retention window.
      if (w.config.type === 'LIVE_FEED') continue;
      const wMax = getWidgetLookbackMs(w.config.horizon, w.thresholds ?? [], dayParts);
      if (wMax > max) max = wMax;
    }
    return max;
  }, [selectedLayout.widgets, dayParts]);

  const { isLoadingHistoricalEvents, sseStatus, lastEventReceivedAt, consumeAvgEventLatencyMs } =
    useServiceDisplayEvents(device, eventsController, lookbackMs);

  const [liveFeedTelemetry] = useState(() => new LiveFeedTelemetry());
  useServiceDisplayHeartbeat(
    device?.id,
    sseStatus,
    lastEventReceivedAt,
    consumeAvgEventLatencyMs,
    liveFeedTelemetry,
    isStandalone,
  );

  // Publish the current device's accessible address so the testing debugger (a sibling
  // of the monitor) can inject simulated events into this device's backend pipeline.
  const { setInjectionBaseUrl } = useServiceDisplayInjection();
  const authDevice = useAuthDevice(device);
  useEffect(() => {
    setInjectionBaseUrl(authDevice?.accessibleAddress || null);
  }, [setInjectionBaseUrl, authDevice?.accessibleAddress]);

  const aiTagConfigurationsMap = useSiteSpecificVisionAnalyticsCamera(siteId);
  const { data: aiTags } = useAiTags();

  // Push AI tag id → name into the controller so summary entries are labelled with the
  // AI tag's own name rather than whichever zone/line label was attached to the first
  // event seen for that tag. Re-runs whenever the tag list updates (e.g. after a rename).
  useEventsControllerTagNamesSetter(eventsController);

  const shouldAddWidget = !!location.state?.addWidget;

  const isDialogVisible = useControllerState(
    controller.state,
    (state) => state.isLayoutDialogVisible,
  );

  // Sync up the controller's editing state with the URL
  useEffect(() => {
    const editConditions = editMode && !editingLayout;
    if (editConditions && shouldAddWidget) {
      controller.layoutEditStart(currentLayout.id);
      controller.updateLayoutWithNewConfig();
    } else if (editConditions) {
      controller.layoutEditStart(currentLayout.id);
    } else if (!editMode && editingLayout && !isDialogVisible) {
      controller.discardLayoutEdits();
    }
  }, [editMode, editingLayout, controller, currentLayout, shouldAddWidget, isDialogVisible]);

  const committedRows = useMemo(
    () => groupWidgetsIntoRows(selectedLayout.widgets),
    [selectedLayout.widgets],
  );

  const [activeId, setActiveId] = useState<string | null>(null);
  const [insertionPoint, setInsertionPoint] = useState<InsertionPoint | null>(null);
  // Widget the cursor hovers when no insertion is in range — dropping swaps the
  // two. Mutually exclusive with insertionPoint.
  const [swapTargetId, setSwapTargetId] = useState<string | null>(null);

  // Compose our own ref with react-cool-dimensions' observe callback so both
  // track the same node.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const setContainerRef = useCallback(
    (el: HTMLDivElement | null) => {
      gridRef(el);
      containerRef.current = el;
    },
    [gridRef],
  );
  // Mirror the insertion point and swap target in refs so the drop handler reads
  // the latest values regardless of render timing.
  const insertionPointRef = useRef<InsertionPoint | null>(null);
  const swapTargetRef = useRef<string | null>(null);
  const widgetElementsRef = useRef<Map<string, HTMLDivElement>>(new Map());
  const pendingFlipFirstRectsRef = useRef<Map<string, DOMRect> | null>(null);
  const [layoutFlip, setLayoutFlip] = useState<Map<string, { x: number; y: number }>>(new Map());
  const [animateLayout, setAnimateLayout] = useState(false);

  const registerWidgetRef = useCallback((id: string, el: HTMLDivElement | null) => {
    if (el) widgetElementsRef.current.set(id, el);
    else widgetElementsRef.current.delete(id);
  }, []);

  const captureWidgetRects = useCallback(() => {
    const rects = new Map<string, DOMRect>();
    for (const [id, el] of widgetElementsRef.current) {
      rects.set(id, el.getBoundingClientRect());
    }
    return rects;
  }, []);

  useLayoutEffect(() => {
    const firstRects = pendingFlipFirstRectsRef.current;
    if (!firstRects) return;
    pendingFlipFirstRectsRef.current = null;

    const inverted = new Map<string, { x: number; y: number }>();
    for (const [id, first] of firstRects) {
      const el = widgetElementsRef.current.get(id);
      if (!el) continue;
      const last = el.getBoundingClientRect();
      const x = first.left - last.left;
      const y = first.top - last.top;
      if (Math.abs(x) < 1 && Math.abs(y) < 1) continue;
      inverted.set(id, { x, y });
    }

    if (inverted.size === 0) {
      setAnimateLayout(false);
      return;
    }

    setAnimateLayout(true);
    setLayoutFlip(inverted);

    requestAnimationFrame(() => {
      setLayoutFlip(new Map());
    });

    const timer = window.setTimeout(() => setAnimateLayout(false), LAYOUT_TRANSITION_MS);
    return () => window.clearTimeout(timer);
  }, [committedRows]);

  const activeWidget = useMemo(
    () => (activeId ? selectedLayout.widgets.find((w) => w.id === activeId) : undefined),
    [activeId, selectedLayout.widgets],
  );

  const previewRows = useMemo(
    () =>
      activeId && insertionPoint
        ? applyInsertionPoint(committedRows, activeId, insertionPoint)
        : null,
    [activeId, insertionPoint, committedRows],
  );

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const dragDisabled = !editing || isLoadingHistoricalEvents;

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveId(String(active.id));
    setInsertionPoint(null);
    insertionPointRef.current = null;
  };

  const handleDragMove = ({ active, delta, activatorEvent }: DragMoveEvent) => {
    const container = containerRef.current;
    if (!container || committedRows.length === 0) return;

    const rect = container.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const origin = activatorEvent as PointerEvent;
    const pointer = {
      x: origin.clientX + delta.x - rect.left,
      y: origin.clientY + delta.y - rect.top,
    };

    // Offer a new-row target when a row still fits, or when the dragged widget
    // is alone in its row (moving it out empties that row, so net row count
    // does not grow).
    const activeRow = committedRows.find((row) => row.some((w) => w.id === String(active.id)));
    const canAddRow = committedRows.length < SDM_GRID_ROWS || activeRow?.length === 1;

    const point = pointerToInsertionPoint(
      committedRows,
      pointer,
      rect.width,
      rect.height,
      canAddRow,
      INSERT_BUFFER_PX,
    );

    // A point near a boundary can still be a no-op (e.g. dropped beside itself),
    // so only treat it as an insert when it actually rearranges the grid.
    const insertPoint =
      point && applyInsertionPoint(committedRows, String(active.id), point) ? point : null;

    // With no valid insert, fall back to swapping with whatever widget the cursor
    // sits on (never the dragged widget itself).
    let swapTarget: string | null = null;
    if (!insertPoint) {
      const target = pointerToWidget(committedRows, pointer, rect.width, rect.height);
      if (target?.id && target.id !== String(active.id)) swapTarget = target.id;
    }

    insertionPointRef.current = insertPoint;
    swapTargetRef.current = swapTarget;
    setInsertionPoint((prev) => (insertionPointsEqual(prev, insertPoint) ? prev : insertPoint));
    setSwapTargetId((prev) => (prev === swapTarget ? prev : swapTarget));
  };

  const resetDrag = () => {
    setActiveId(null);
    setInsertionPoint(null);
    setSwapTargetId(null);
    insertionPointRef.current = null;
    swapTargetRef.current = null;
  };

  const handleDragEnd = ({ active }: DragEndEvent) => {
    const point = insertionPointRef.current;
    const swapId = swapTargetRef.current;
    const activeIdStr = String(active.id);

    let result: Row[] | null = null;
    if (point) {
      result = applyInsertionPoint(committedRows, activeIdStr, point);
    } else if (swapId) {
      result = swapWidgets(committedRows, activeIdStr, swapId);
    }

    if (result) {
      pendingFlipFirstRectsRef.current = captureWidgetRects();
      controller.commitWidgetRows(result.map((row) => row.map((w) => w.id ?? '')));
    }
    resetDrag();
  };

  const handleDragCancel = () => {
    resetDrag();
  };

  const startEditingWithNewWidget = React.useCallback(() => {
    if (editingLayout) {
      controller.updateLayoutWithNewConfig();
    } else {
      navigate(`${SDM_CONFIG_ROUTE}${location.search}`, { state: { addWidget: true } });
    }
  }, [controller, editingLayout, navigate, location.search]);

  if (!selectedLayout.widgets.length) {
    return <EmptyLayout addAction={editMode ? startEditingWithNewWidget : undefined} />;
  }

  const activeWidgetPixelWidth = activeWidget ? (activeWidget.position.w / 100) * width : 0;
  const activeWidgetPixelHeight = activeWidget ? (activeWidget.position.h / 100) * height : 0;

  const activeInsertionPoint = previewRows ? insertionPoint : null;
  const rowHeightPx = committedRows.length > 0 ? height / committedRows.length : 0;

  return (
    <LiveFeedTelemetryProvider telemetry={liveFeedTelemetry}>
      <div className="relative h-full min-h-0 overflow-hidden" ref={setContainerRef}>
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragMove={handleDragMove}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <div className="flex h-full w-full flex-col overflow-hidden">
            {committedRows.map((row, rowIndex) => (
              <GridWidgetRow
                key={rowIndex}
                row={row}
                rowIndex={rowIndex}
                rowHeightPx={rowHeightPx}
                rowCount={committedRows.length}
                activeInsertionPoint={activeInsertionPoint}
                activeId={activeId}
                swapTargetId={swapTargetId}
                layoutFlip={layoutFlip}
                animateLayout={animateLayout}
                dragDisabled={dragDisabled}
                registerWidgetRef={registerWidgetRef}
                controller={controller}
                eventsController={eventsController}
                allTags={aiTags ?? []}
                aiTagConfigurationMap={aiTagConfigurationsMap}
                isDataLoading={isLoadingHistoricalEvents}
              />
            ))}
          </div>
          {activeInsertionPoint && (
            <InsertionBar
              point={activeInsertionPoint}
              rows={committedRows}
              rowHeightPx={rowHeightPx}
              widthPx={width}
              heightPx={height}
            />
          )}
          <DragOverlay>
            {activeWidget && (
              <div
                className="box-border overflow-hidden rounded-lg opacity-50 ring-2 ring-primary"
                style={{
                  width: activeWidgetPixelWidth,
                  height: activeWidgetPixelHeight,
                }}
              >
                <Widget
                  controller={controller}
                  eventsController={eventsController}
                  allTags={aiTags ?? []}
                  aiTagConfigurationMap={aiTagConfigurationsMap}
                  widget={activeWidget}
                  isDataLoading={isLoadingHistoricalEvents}
                  isDragging
                />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      </div>
    </LiveFeedTelemetryProvider>
  );
}
